import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ModalActionsData } from 'store/store.types';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';
import { cn } from 'components/lib/utils';
import Typography from 'components/Typography/Typography';

import { IBlock } from '../constants';
import { blocksV2ArraySchema, pageFormSchema } from '../validation-schema';

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

const cleanBlocks = (blocks: IBlock[]): IBlock[] => {
  const cleanedBlocks = blocks.map((block) => {
    // Create a new block object without asset fields
    const cleanedBlock: any = { ...block };

    // Remove backgroundImage and backgroundImageMobile from block level
    delete cleanedBlock.backgroundImage;
    delete cleanedBlock.backgroundImageMobile;
    delete cleanedBlock.assetUrl;
    delete cleanedBlock.assetUrlMobile;

    if (cleanedBlock.items && Array.isArray(cleanedBlock.items)) {
      cleanedBlock.items = cleanedBlock.items.map((item: any) => {
        const cleanedItem: any = { ...item };

        if (item.type === 'image') {
          delete cleanedItem.url;
          delete cleanedItem.mobileUrl;
        }

        delete cleanedItem.assetUrl;
        delete cleanedItem.assetUrlMobile;

        return cleanedItem;
      });
    }

    return cleanedBlock;
  });
  return cleanedBlocks;
};

export const BlocksImport: React.FC = () => {
  const { portalId } = useParams();
  const { setValue, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [blocksData, setBlocksData] = useState<IBlock[] | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      showToast({
        message: 'Please select a valid JSON file',
        status: EToasterStatus.ERROR
      });
      return;
    }

    try {
      const fileContent = await readFileAsText(file);
      const parsedData = JSON.parse(fileContent);

      const parsedBlocks = parsedData.blocks;

      if (!Array.isArray(parsedBlocks)) {
        throw new Error('Invalid format: blocks must be an array');
      }

      const validationResult = blocksV2ArraySchema.safeParse(parsedBlocks);

      if (!validationResult.success) {
        const errorMessages = validationResult.error.errors
          .map((err: any) => {
            const path = err.path.length > 0 ? err.path.join('.') : 'root';
            return `${path}: ${err.message}`;
          })
          .join(', ');
        throw new Error(`Blocks validation failed: ${errorMessages}`);
      }

      const validatedBlocks: IBlock[] =
        parsedData.portalId !== portalId
          ? cleanBlocks(validationResult.data as IBlock[])
          : (validationResult.data as IBlock[]);

      setSelectedFile(file);
      setBlocksData(validatedBlocks);
      trigger('blocksV2');
    } catch (error) {
      console.error('Import error:', error);

      let errorMessage =
        'Failed to import blocks. Please check the file format.';

      if (error instanceof Error) {
        if (error.message.includes('Blocks validation failed:')) {
          errorMessage = `Blocks validation failed. ${error.message.replace('Blocks validation failed: ', '')}`;
        } else if (error.message.includes('Invalid format')) {
          errorMessage = error.message;
        } else if (error.message.includes('JSON')) {
          errorMessage =
            'Invalid JSON format. Please ensure the file contains valid JSON.';
        }
      }

      setBlocksData(null);
      setSelectedFile(null);
      setIsImporting(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      showToast({
        message: errorMessage,
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleImport = useCallback(async () => {
    if (!blocksData) {
      showToast({
        message: 'Please select a file to import',
        status: EToasterStatus.ERROR
      });
      return;
    }

    setIsImporting(true);

    try {
      // Replace all existing blocks with imported blocks
      setValue('blocksV2', blocksData, {
        shouldValidate: true,
        shouldDirty: true
      });

      showToast({
        message: 'Blocks imported successfully',
        status: EToasterStatus.SUCCESS
      });

      handleClose();
    } catch (error) {
      console.error('Import error:', error);
      showToast({
        message: 'Failed to import blocks',
        status: EToasterStatus.ERROR
      });
      setIsImporting(false);
    }
  }, [blocksData, setValue]);

  const handleClose = () => {
    setBlocksData(null);
    setSelectedFile(null);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsImportModalOpen(false);
  };

  const modalActions: Record<string, ModalActionsData> = useMemo(
    () => ({
      onConfirm: {
        label: isImporting ? 'Importing...' : 'Import',
        action: handleImport,
        disabled: !blocksData || isImporting
      },
      onClose: {
        label: 'Cancel',
        action: handleClose,
        disabled: isImporting,
        variant: 'ghost'
      }
    }),
    [blocksData, isImporting, handleImport]
  );

  const handleClickOnImportButton = () => {
    setIsImportModalOpen(true);
  };

  return (
    <>
      <Button
        onClick={handleClickOnImportButton}
        variant="outline"
        type="button"
        className="text-sm w-[100px] text-base-secondaryForeground"
        leftIcon={<FontAwesomeIcon icon="fa-light fa-download" />}
      >
        Import
      </Button>
      {isImportModalOpen && (
        <DialogModal
          isOpen={isImportModalOpen}
          title="Import Blocks"
          description={`Select a JSON file containing blocks to import. This will replace all existing blocks.`}
          modalActions={modalActions}
        >
          <div className="flex flex-col gap-4">
            <div
              className={cn(
                'flex flex-col items-center justify-center p-8 border border-dashed border-base-muted-foreground rounded-lg cursor-pointer hover:border-baseForeground transition-colors h-[190px]',
                selectedFile && 'bg-base-muted'
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md text-center">
                  <FontAwesomeIcon
                    icon="fa-solid fa-file"
                    className="text-baseForeground mb-6"
                  />
                  <Typography className="text-sm font-medium text-baseForeground">
                    {selectedFile.name}
                  </Typography>
                  <Typography className="text-xs text-base-mutedForeground font-semibold">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </div>
              ) : (
                <>
                  <img
                    src={'./assets/icons/ac-cloud-upload.png'}
                    alt="File"
                    className="w-[48px] h-[48px] mb-7"
                  />
                  <Typography className="text-sm text-base-mutedForeground text-center">
                    {'Select a blocks JSON file from your computer'}
                  </Typography>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </DialogModal>
      )}
    </>
  );
};
